import {
  Box,
  MenuItem,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Typography,
  FormHelperText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { useLocalization } from '@/hooks/useLocalization';
import { isCapacitorApp } from '@/utils/isCapacitorApp';
import {
  US_STATE_OPTIONS,
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
} from '@/components/flyouts/ClientDetailsFlyout/constants';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { WARootState } from '@/store/store';
import { useGetAdvisorGroupSettingsQuery } from '@/store/settingsApi';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { useModuleAccess } from '@/permissions';
import { useIntegration } from '@/permissions/hooks/useIntegration';
import { MaskedSocialSecurityNumberInput } from '@/components/common/MaskedSocialSecurityNumberInput';
import AdvisorGroupSettings from '@/utils/advisorGroupSettings.utility';
import { AdvisorInvestorSetup } from '@/store/usersApi';
import { type ClientFormValues } from '@/modules/clientServices/clientForm.helpers';

// Narrow view of integrations keys we use
type IntegrationsSubset = Partial<
  Record<'MGP' | 'TD', boolean | number | string>
>;

type ClientFormProps = {
  /** Existing record used to seed the form (undefined when creating). */
  submittedData?: AdvisorInvestorSetup;
  /** Disables inputs while the owning view persists the values. */
  isSaving?: boolean;
  /** Emits the validated form values; the owning view builds the payload and submits. */
  onSubmit: (values: ClientFormValues) => Promise<void>;
  /** Notifies the owning view of validity so it can enable/disable its submit controls. */
  onValidityChange?: (isValid: boolean) => void;
  formId: string;
  /** `prospect` follows the Add Prospect field order and shows the profile fields with the MoneyGuidePro integration. */
  variant?: 'client' | 'prospect';
  /** Emits the labels of the fields that block saving, in display order. */
  onInvalidFieldsChange?: (labels: string[]) => void;
};

/** Imperative handle exposed to owning views (e.g. reset after Save & Add Another). */
export type ClientFormHandle = { reset: () => void };

/**
 * Reusable client form. Owns all field state and validation; it does NOT own
 * submission. On submit it emits the validated values via `onSubmit` and the
 * owning view decides how to persist them (create vs. edit, snackbar, reset,
 * error handling). Wire a submit control to it with `form={CLIENT_FORM_ID}`.
 */
const ClientForm = React.forwardRef<ClientFormHandle, ClientFormProps>(
  (
    {
      submittedData,
      isSaving = false,
      onSubmit,
      onValidityChange,
      formId,
      variant = 'client',
      onInvalidFieldsChange,
    },
    ref,
  ) => {
    const { t } = useLocalization();

    const userInfo = useSelector((s: WARootState) => s.appView.UserInfo);
    const {
      data: advisorGroupSettings,
      isLoading: isLoadingSettings,
      isError: settingsFailed,
    } = useGetAdvisorGroupSettingsQuery();

    // --- Gates derived from legacy logic ---
    const { validateByModule, validateByFeature } = useModuleAccess();
    const integrations = (userInfo?.Integrations || {}) as IntegrationsSubset;

    const tdEnabled = Boolean(integrations['TD']);
    const tssEnabled = validateByModule('TSSExternalPartyId');

    const showUserProfile =
      validateByModule('clients') && validateByFeature('userprofile');
    const showTdField = tdEnabled;
    const showInterestedPartyField = tssEnabled;

    const { isIntegrationEnabled } = useIntegration();
    const isProspect = variant === 'prospect';
    const showProfileFields = isProspect
      ? isIntegrationEnabled('MGP')
      : showUserProfile;

    // Legacy parity with AdvisorGroupSettings: last name required only for prospects when setting is enabled
    // A prospect form without the settings applies the stricter rule.
    const isLastNameRequired: boolean = React.useMemo(() => {
      if (isProspect && settingsFailed) {
        return true;
      }
      if (!advisorGroupSettings) {
        return false;
      }

      return (
        AdvisorGroupSettings(advisorGroupSettings).getByKey(
          'RequireLastNameForProspects',
          'Value',
        ) ?? false
      );
    }, [advisorGroupSettings, isProspect, settingsFailed]);
    const waitsForSettings = isProspect && isLoadingSettings;

    const validationSchema = React.useMemo(
      () =>
        yup.object({
          firstName: yup.string().trim().required(t('validation.required')),
          email: yup
            .string()
            .trim()
            .email(t('validation.invalidEmail'))
            .required(t('validation.required')),
          lastName: isLastNameRequired
            ? yup.string().trim().required(t('validation.required'))
            : yup.string().trim(),
          ssn: yup
            .string()
            .transform((v: string) => (v.trim() === '' ? undefined : v))
            .min(9, t('clientForm.ssnLength')),
          dob: yup
            .mixed<Dayjs>()
            .nullable()
            .test(
              'validDate',
              t('clientForm.dobInvalid', 'Enter a complete, valid date.'),
              (value) => !value || value.isValid(),
            )
            .test(
              'notInFuture',
              t(
                'clientForm.dobInFuture',
                'Date of birth cannot be in the future.',
              ),
              (value) =>
                !value || !value.isValid() || !value.isAfter(dayjs(), 'day'),
            ),
          stateCode: yup.string().trim(),
          gender: yup
            .mixed<ClientFormValues['gender']>()
            .oneOf(['', ...GENDER_OPTIONS.map((g) => g.id)]),
          maritalStatus: yup
            .mixed<ClientFormValues['maritalStatus']>()
            .oneOf(['', ...MARITAL_STATUS_OPTIONS.map((m) => m.id)]),
          tdAceUserId: yup.string().trim(),
          externalId: yup.string().trim(),
        }),
      [isLastNameRequired, t],
    );

    const formik = useFormik<ClientFormValues>({
      enableReinitialize: true,
      validateOnMount: true,
      validationSchema,
      initialValues: {
        firstName: submittedData?.FirstName || '',
        lastName: submittedData?.LastName || '',
        email: submittedData?.UserName || '',
        ssn: submittedData?.SocialSecurityNumber || '',
        dob: submittedData?.DOB ? dayjs(submittedData.DOB) : null,
        stateCode: submittedData?.State || '',
        gender: submittedData?.Gender || '',
        maritalStatus: submittedData?.MaritalStatus || '',
        tdAceUserId: submittedData?.TDAceUserId || '',
        externalId: submittedData?.ExternalId || '',
      },
      onSubmit: async (values) => {
        await onSubmit(values);
      },
    });

    const { validateForm } = formik;
    useEffect(() => {
      void validateForm();
    }, [isLastNameRequired, validateForm]);

    // Keep the owning view's submit controls in sync with form validity.
    useEffect(() => {
      onValidityChange?.(formik.isValid && !waitsForSettings);
    }, [formik.isValid, waitsForSettings, onValidityChange]);

    const { errors } = formik;
    useEffect(() => {
      if (!onInvalidFieldsChange) return;
      const fieldLabels: [keyof ClientFormValues, string][] = [
        ['firstName', t('forms.firstName', 'First Name')],
        ['lastName', t('forms.lastName', 'Last Name')],
        ['email', t('forms.usernameEmailAddress', 'Username (Email Address)')],
        ['ssn', t('forms.socialSecurityNumber', 'Social Security Number')],
        ['dob', t('forms.dateOfBirth', 'Date of Birth')],
      ];
      onInvalidFieldsChange(
        fieldLabels
          .filter(([field]) => Boolean(errors[field]))
          .map(([, label]) => label),
      );
    }, [errors, onInvalidFieldsChange, t]);

    // Let owning views reset the form (e.g. after Save & Add Another).
    React.useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          formik.resetForm();
          void formik.validateForm(formik.initialValues);
        },
      }),
      [formik],
    );

    const ssnField = (
      <MaskedSocialSecurityNumberInput
        name='ssn'
        label={t('forms.socialSecurityNumber', 'Social Security Number')}
        value={formik.values.ssn}
        onValueChange={(value) => void formik.setFieldValue('ssn', value)}
        error={formik.touched.ssn && Boolean(formik.errors.ssn)}
        errorText={formik.errors.ssn}
        onBlur={formik.handleBlur}
        disabled={isSaving}
      />
    );

    const profileFields = showProfileFields && (
      <>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
        >
          <DatePicker
            label={t('forms.dateOfBirth', 'Date of Birth')}
            value={formik.values.dob}
            onChange={(date) => void formik.setFieldValue('dob', date, true)}
            maxDate={dayjs()}
            enableAccessibleFieldDOMStructure={!isCapacitorApp()}
            slotProps={{
              textField: {
                fullWidth: true,
                error: Boolean(formik.errors.dob),
                helperText: formik.errors.dob,
                placeholder: t('clientForm.dobPlaceholder'), // Display MM/DD/YYYY, convert to M/D/YYYY on submit for legacy parity with existing API
                inputProps: isCapacitorApp() ? { readOnly: true } : undefined,
              },
            }}
          />

          <FormControl fullWidth>
            <InputLabel>{t('forms.state', 'State')}</InputLabel>
            <Select
              label={t('forms.state', 'State')}
              value={formik.values.stateCode}
              onChange={(e) =>
                void formik.setFieldValue('stateCode', e.target.value, true)
              }
            >
              <MenuItem value=''>
                {t('forms.selectState', 'Select state')}
              </MenuItem>
              {US_STATE_OPTIONS.map((s) => (
                <MenuItem
                  key={s.code}
                  value={s.code}
                >
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
        >
          <FormControl fullWidth>
            <InputLabel>{t('forms.gender.label', 'Gender')} </InputLabel>
            <Select
              label={t('forms.gender.label', 'Gender')}
              value={formik.values.gender}
              onChange={(e) =>
                void formik.setFieldValue('gender', e.target.value, true)
              }
              disabled={isSaving}
            >
              <MenuItem value=''>
                {t('forms.gender.select', 'Select gender')}
              </MenuItem>
              {GENDER_OPTIONS.map((g) => (
                <MenuItem
                  key={g.id}
                  value={g.id}
                >
                  {t(g.translation_key, g.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>
              {t('forms.maritalStatus', 'Marital Status')}{' '}
            </InputLabel>
            <Select
              label={t('forms.maritalStatus', 'Marital Status')}
              value={formik.values.maritalStatus}
              onChange={(e) =>
                void formik.setFieldValue('maritalStatus', e.target.value, true)
              }
            >
              <MenuItem value=''>
                {t('forms.maritalStatusSelect', 'Select')}
              </MenuItem>
              {MARITAL_STATUS_OPTIONS.map((m) => (
                <MenuItem
                  key={m.id}
                  value={m.id}
                >
                  {t(m.translation_key, m.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </>
    );

    const tdField = showTdField && (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={3}
      >
        <TextField
          name='tdAceUserId'
          fullWidth
          label={t('forms.trustDeskAceUserId', 'TrustDesk AceUserId')}
          placeholder={t('forms.trustDeskAceUserId', 'TrustDesk AceUserId')}
          value={formik.values.tdAceUserId}
          onChange={formik.handleChange}
          disabled={isSaving}
        />
      </Stack>
    );

    const externalIdField = showInterestedPartyField && (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={3}
      >
        <TextField
          name='externalId'
          fullWidth
          label={t(
            'forms.interestedPartyId',
            'Interested Party ID for Statement Delivery',
          )}
          placeholder={t(
            'forms.interestedPartyId',
            'Interested Party ID for Statement Delivery',
          )}
          value={formik.values.externalId}
          onChange={formik.handleChange}
          disabled={isSaving}
        />
      </Stack>
    );

    return (
      <form
        onSubmit={formik.handleSubmit}
        id={formId}
      >
        <Box>
          <Stack spacing={3}>
            {/* First Name */}
            <TextField
              name='firstName'
              fullWidth
              required
              label={t('forms.firstName', 'First Name')}
              placeholder={t('forms.firstName', 'First Name')}
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
              disabled={isSaving}
            />

            {/* Last Name */}
            <TextField
              name='lastName'
              fullWidth
              required={isLastNameRequired}
              label={t('forms.lastName', 'Last Name')}
              placeholder={t('forms.lastName', 'Last Name')}
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={
                formik.touched.lastName &&
                typeof formik.errors.lastName === 'string'
                  ? formik.errors.lastName
                  : undefined
              }
              disabled={isSaving}
            />

            {/* Email */}
            <Box>
              <TextField
                name='email'
                fullWidth
                required
                type='email'
                label={t(
                  'forms.usernameEmailAddress',
                  'Username (Email Address)',
                )}
                placeholder={t('email', 'Email')}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                disabled={isSaving}
              />
              {submittedData?.UserName && (
                <FormHelperText>
                  <Typography
                    variant='caption'
                    color='error'
                  >
                    {t(
                      'usernameChangeWarning',
                      'Changing the username will cause the previous username to no longer work.',
                    )}
                  </Typography>
                </FormHelperText>
              )}
              <FormHelperText>
                <Typography
                  variant='caption'
                  color='error'
                >
                  {formik.touched.email &&
                    typeof formik.errors.email === 'string' &&
                    formik.errors.email}
                </Typography>
              </FormHelperText>
            </Box>

            {isProspect ? (
              <>
                {tdField}
                {externalIdField}
                {ssnField}
                {showProfileFields && <Divider />}
                {profileFields}
              </>
            ) : (
              <>
                {ssnField}
                {profileFields}
                {(showTdField || showInterestedPartyField) && <Divider />}
                {tdField}
                {externalIdField}
              </>
            )}
          </Stack>
        </Box>
      </form>
    );
  },
);

ClientForm.displayName = 'ClientForm';

export default ClientForm;
